// Document previews. The sender renders a PDF's first page into a small JPEG
// (it travels inside the end-to-end encrypted message, like a photo's
// thumbnail) and counts its pages; the viewer renders PDFs with pdf.js (the
// app's WebView has no PDF viewer of its own) and shows text files as text.
// pdf.js is loaded only when a PDF is sent or opened.
import type * as PdfJs from "pdfjs-dist";

let lib: Promise<typeof PdfJs> | null = null;
function pdfjs(): Promise<typeof PdfJs> {
  // The worker starts from a blob with an explicit JavaScript type: the app's
  // asset server may not serve .mjs files as JavaScript.
  return (lib ??= Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?raw")]).then(([m, worker]) => {
    m.GlobalWorkerOptions.workerSrc = URL.createObjectURL(new Blob([worker.default], { type: "text/javascript" }));
    return m;
  }));
}

export const isPdf = (mime: string, name?: string) => mime === "application/pdf" || /\.pdf$/i.test(name ?? "");

const TEXT_EXT = /\.(txt|md|csv|tsv|json|log|xml|ya?ml|ini|conf|toml|js|mjs|ts|tsx|jsx|py|go|java|kt|c|cc|cpp|h|hpp|rs|rb|php|sh|sql|css|html?|srt|vtt)$/i;
export const isText = (mime: string, name?: string) =>
  (mime.startsWith("text/") && mime !== "text/html") || mime === "application/json" || mime === "application/xml" || TEXT_EXT.test(name ?? "");

/** Can the in-app viewer show this document? */
export const previewable = (mime: string, name?: string) => isPdf(mime, name) || isText(mime, name);

/** Open a PDF; `close()` frees it (and its worker resources). */
export async function openPdf(data: ArrayBuffer) {
  const m = await pdfjs();
  const task = m.getDocument({ data: new Uint8Array(data), enableXfa: false });
  return { doc: await task.promise, close: () => void task.destroy() };
}

const PREVIEW_W = 320;
const PREVIEW_MAX_H = 240; // the top of the first page, like WhatsApp

/** Sender: the first page (top part) as a JPEG data URL, and the page count. */
export async function pdfPreview(file: Blob): Promise<{ thumb?: string; pages: number }> {
  const { doc, close } = await openPdf(await file.arrayBuffer());
  try {
    const pages = doc.numPages;
    const page = await doc.getPage(1);
    const vp = page.getViewport({ scale: PREVIEW_W / page.getViewport({ scale: 1 }).width });
    const full = document.createElement("canvas");
    full.width = Math.ceil(vp.width);
    full.height = Math.ceil(vp.height);
    const ctx = full.getContext("2d")!;
    ctx.fillStyle = "#fff"; // PDFs are transparent: paper is white
    ctx.fillRect(0, 0, full.width, full.height);
    await page.render({ canvas: full, canvasContext: ctx, viewport: vp }).promise;
    const out = document.createElement("canvas");
    out.width = full.width;
    out.height = Math.min(full.height, PREVIEW_MAX_H);
    out.getContext("2d")!.drawImage(full, 0, 0);
    // Stay well inside what a message may carry (60 kB).
    for (const q of [0.7, 0.5, 0.35]) {
      const url = out.toDataURL("image/jpeg", q);
      if (url.length < 50_000) return { thumb: url, pages };
    }
    return { pages };
  } finally {
    close();
  }
}

/** Viewer: render every page (up to `max`) into canvases `width` CSS px
 * wide, as long as `alive()`; call `close()` on the result when done. */
export async function renderPdf(data: ArrayBuffer, width: number, onPage: (c: HTMLCanvasElement, n: number, total: number) => void, alive: () => boolean, max = 100) {
  const { doc, close } = await openPdf(data);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  for (let n = 1; n <= Math.min(doc.numPages, max) && alive(); n++) {
    const page = await doc.getPage(n);
    const vp = page.getViewport({ scale: (width * dpr) / page.getViewport({ scale: 1 }).width });
    const c = document.createElement("canvas");
    c.width = Math.ceil(vp.width);
    c.height = Math.ceil(vp.height);
    c.style.width = width + "px";
    const ctx = c.getContext("2d")!;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, c.width, c.height);
    await page.render({ canvas: c, canvasContext: ctx, viewport: vp }).promise;
    onPage(c, n, doc.numPages);
  }
  return { pages: doc.numPages, close };
}
